import { createStyles, makeStyles, Theme } from '@material-ui/core';
import { Editor } from '@tiptap/core';
import clsx from 'clsx';
import { Button } from 'copilot-design-system';
import React, { useContext, useEffect } from 'react';
import {
  AttachmentPreviewList,
  MessageInputProps,
  TextareaComposer,
  useAttachmentManagerState,
  useMessageComposer,
  useMessageContext,
  useMessageInputContext,
} from 'stream-chat-react';

import { ErrorText } from 'src/components/ErrorText';
import { FlagsContext } from 'src/context';
import {
  MAX_CHARACTERS_LIMIT,
  MAX_CHARACTERS_LIMIT_ERROR,
} from 'src/features/channel/channel-content/ChannelContent';
import { MessageRichTextInput } from 'src/features/channel/custom-message-input/MessageRichTextInput';
import { HoverBackground, NonHoverBorder } from 'src/theme/colors';
import { isEmptyString } from 'src/utils/StringUtils';

import { useMessageComponents } from './useMessageComponents';

interface StylesProps {
  showAvatar: boolean;
}
const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    editMessageFormWrapper: {
      padding: (props: StylesProps) =>
        theme.spacing(1.5, 9, 6.25, props.showAvatar ? 3.5 : 9),
      backgroundColor: HoverBackground,
      '& .rfu-image-previewer': {
        margin: 0,
      },

      '& .str-chat__textarea textarea': {
        border: `1px solid ${NonHoverBorder}`,
        borderRadius: theme.shape.borderRadius,
        padding: theme.spacing(1, 1.5),
        '&:focus': {
          border: `1px solid ${NonHoverBorder}`,
          boxShadow: 'none',
        },
      },
    },
    submitButton: {
      marginLeft: theme.spacing(2.5),
    },
  }),
);

interface EditMessageFormProps {
  handleDelete: () => void;
}

export const EditMessageForm: React.FC<
  EditMessageFormProps & MessageInputProps
> = ({ handleDelete }) => {
  const { EnableMessageEditorInput } = useContext(FlagsContext);
  const { avatarRenderer, isNewMessageGroup: showAvatar } =
    useMessageComponents();
  const classes = useStyles({ showAvatar: !!showAvatar });
  const { focus, handleSubmit, textareaRef, onPaste, clearEditingState } =
    useMessageInputContext();
  const { message } = useMessageContext();
  const { isUploadEnabled, successfulUploadsCount } =
    useAttachmentManagerState();
  const messageComposer = useMessageComposer();
  const { text } = messageComposer.textComposer;
  const { attachments } = messageComposer.attachmentManager;
  const [draftAttachments, setDraftAttachments] = React.useState<
    typeof attachments
  >(() => []);
  const [originalText, setOriginalText] = React.useState('');

  const handleRichTextContentChange = async (editor: Editor) => {
    await messageComposer.textComposer.handleChange({
      selection: {
        end: editor.state.selection.$to.pos,
        start: editor.state.selection.$from.pos,
      },
      text: editor.storage.markdown.getMarkdown() ?? '',
    });
  };

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.maxLength = MAX_CHARACTERS_LIMIT;
    }
  }, [textareaRef.current]);

  React.useEffect(() => {
    if (focus && textareaRef.current) {
      textareaRef.current.selectionStart += text.length;
      textareaRef.current.focus();
    }
  }, [focus]);

  const handleEdit = (event: React.BaseSyntheticEvent) => {
    event.preventDefault();
    const isEmptyMessageText = isEmptyString(text);
    if (isEmptyMessageText && successfulUploadsCount === 0) {
      handleDelete();
      return;
    }
    handleSubmit(event);
  };

  // Capture original values when a new message edit starts
  useEffect(() => {
    if (message?.id) {
      setDraftAttachments([...attachments]);
      setOriginalText(text);
    }
  }, [message?.id]);

  const handleCancel = () => {
    if (clearEditingState) {
      messageComposer.textComposer.handleChange({
        text: originalText,
        selection: {
          start: originalText.length,
          end: originalText.length,
        },
      });
      const uploadsIds = messageComposer.attachmentManager.attachments.map(
        (attachment) => attachment.localMetadata.id,
      );
      messageComposer.attachmentManager.removeAttachments(uploadsIds);
      messageComposer.attachmentManager.upsertAttachments(draftAttachments);
      clearEditingState();
    }
  };

  return (
    <div className={classes.editMessageFormWrapper}>
      <form onSubmit={handleEdit}>
        <div className="flex w-full items-start">
          {showAvatar && avatarRenderer()}
          <div
            className={clsx('w-full', {
              'ml-3': showAvatar,
              'ml-0': !showAvatar,
            })}
          >
            {isUploadEnabled && <AttachmentPreviewList />}
            {EnableMessageEditorInput ? (
              <div className="border border-solid border-non-hover-border focus-within:border-black-heading rounded-xl bg-white">
                <MessageRichTextInput
                  key={`edit-rich-textarea-${message?.id}`}
                  content={message?.html ?? message?.text ?? ''}
                  onContentChange={handleRichTextContentChange}
                />
              </div>
            ) : (
              <TextareaComposer
                key={`edit-textarea-${message?.id}`}
                onChange={(e) =>
                  messageComposer.textComposer.handleChange({
                    text: e.target.value,
                    selection: {
                      start: e.target.selectionStart,
                      end: e.target.selectionEnd,
                    },
                  })
                }
                onPaste={onPaste}
                className="w-full resize-none !min-h-[45px]"
                maxRows={10}
              />
            )}
          </div>
        </div>
        {text.length >= MAX_CHARACTERS_LIMIT && (
          <div className="flex justify-end">
            <ErrorText>{MAX_CHARACTERS_LIMIT_ERROR}</ErrorText>
          </div>
        )}
        <div className="mt-3 gap-5 flex w-full items-center justify-end">
          <Button variant="secondary" label="Cancel" onClick={handleCancel} />
          <Button variant="primary" label="Save changes" type="submit" />
        </div>
      </form>
    </div>
  );
};
